import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { godCompanyApi } from '../../api';
import { GS, G, StatusPill, GSection, GField } from './_godTheme';

const STATUSES = ['', 'TRIAL', 'ACTIVE', 'SUSPENDED', 'CHURNED'];

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', slug: '', superAdminEmployeeId: '', superAdminName: '',
    plan: 'TRIAL', subscriptionStart: '', subscriptionEnd: '',
    maxEmployees: 60, maxDepartments: 15, notes: '',
  });
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (v) => {
    set('name', v);
    set('slug', v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
  };

  const validate = () => {
    if (!form.name.trim())                 return 'Company name is required';
    if (!form.slug.trim())                 return 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(form.slug))  return 'Slug must contain only lowercase letters, numbers, and hyphens';
    if (!form.superAdminEmployeeId.trim()) return 'Super Admin Employee ID is required';
    if (!form.superAdminName.trim())       return 'Super Admin name is required';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    try {
      const res = await godCompanyApi.create({ ...form, subscriptionStart: form.subscriptionStart || null, subscriptionEnd: form.subscriptionEnd || null });
      if (res.data.success) { onCreated(res.data.data); onClose(); }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create company');
    } finally { setSaving(false); }
  };

  return (
    <div style={GS.overlay}>
      <div style={GS.modal}>
        <div style={GS.modalHdr}>
          <h3 style={GS.modalTitle}>Create New Company</h3>
          <button onClick={onClose} style={GS.modalClose}>×</button>
        </div>
        {error && <div style={GS.errBox}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <GSection label="Company Identity">
            <GField label="Company Name *">
              <input value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="e.g. Sharma & Associates" style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
            <GField label="URL Slug * (auto-generated)" hint="Unique internal identifier for this company — lowercase, hyphens only. Cannot be changed after creation.">
              <input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="e.g. sharma-associates" style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
          </GSection>

          <GSection label="Super Admin (Tenant Owner)">
            <GField label="Full Name *">
              <input value={form.superAdminName} onChange={e => set('superAdminName', e.target.value)} placeholder="e.g. Rajesh Kumar" style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
            <GField label="Employee ID *" hint="Default password: Tracern@123 — tenant must reset on first login">
              <input value={form.superAdminEmployeeId} onChange={e => set('superAdminEmployeeId', e.target.value)} placeholder="e.g. SA001" style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
          </GSection>

          <GSection label="Subscription & Limits">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <GField label="Start Date"><input type="date" value={form.subscriptionStart} onChange={e => set('subscriptionStart', e.target.value)} style={GS.input} /></GField>
              <GField label="End Date"><input type="date" value={form.subscriptionEnd} onChange={e => set('subscriptionEnd', e.target.value)} style={GS.input} /></GField>
              <GField label="Max Employees"><input type="number" min={1} value={form.maxEmployees} onChange={e => set('maxEmployees', parseInt(e.target.value))} style={GS.input} /></GField>
              <GField label="Max Departments"><input type="number" min={1} value={form.maxDepartments} onChange={e => set('maxDepartments', parseInt(e.target.value))} style={GS.input} /></GField>
            </div>
          </GSection>

          <GSection label="Internal Notes (never visible to tenant)">
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Sales notes, special arrangements, contact details…" rows={2} style={{ ...GS.input, resize: 'vertical' }} />
          </GSection>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
            <button onClick={submit} disabled={saving} style={GS.btnPrimary}>{saving ? 'Creating…' : '+ Create Company'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GodCompanies() {
  const navigate                  = useNavigate();
  const [data,       setData]     = useState({ items: [], total: 0, totalPages: 1 });
  const [loading,    setLoading]  = useState(true);
  const [page,       setPage]     = useState(0);
  const [status,     setStatus]   = useState('');
  const [search,     setSearch]   = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [error,      setError]    = useState('');

  const load = useCallback(() => {
    setLoading(true);
    godCompanyApi.list({ page, size: 20, status: status || undefined, search: search || undefined })
      .then(r => setData(r.data.data))
      .catch(() => setError('Failed to load companies'))
      .finally(() => setLoading(false));
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [status, search]);

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={GS.title}>Companies</h1>
          <p style={GS.sub}>{data.total} tenant{data.total !== 1 ? 's' : ''} total</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={GS.btnPrimary}>+ New Company</button>
      </div>

      {error && <div style={GS.errBox}>{error}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: G.textFaint, fontSize: '14px', pointerEvents: 'none' }}>🔍</span>
          <input placeholder="Search by name or slug…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...GS.input, paddingLeft: '36px' }}
            onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)} style={{
              padding: '7px 14px', borderRadius: '999px', cursor: 'pointer',
              fontSize: '12px', fontWeight: status === s ? 700 : 400,
              background: status === s ? G.gold : '#fff',
              color:      status === s ? '#fff' : G.textMuted,
              border:     `1.5px solid ${status === s ? G.gold : '#E5E7EB'}`,
              transition: 'all 0.12s',
            }}>{s === '' ? 'All' : s}</button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '76px', borderRadius: '14px' }} />)}
        </div>
      ) : !data.items.length ? (
        <div style={{ textAlign: 'center', padding: '60px', color: G.textMuted }}>
          <p style={{ fontSize: '36px', marginBottom: '12px' }}>🏢</p>
          <p style={{ fontWeight: 600, color: G.textSub, marginBottom: '6px' }}>No companies yet</p>
          <p style={{ fontSize: '13px' }}>Create your first tenant to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.items.map(company => (
            <div key={company.id} onClick={() => navigate(`/god/companies/${company.id}`)} style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 18px', background: '#fff', borderRadius: '14px',
              border: '1px solid #E5E7EB', cursor: 'pointer',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = G.goldBorder; e.currentTarget.style.boxShadow = '0 4px 12px rgba(183,121,31,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}
            >
              {/* Logo mark */}
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: G.goldLight, color: G.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, flexShrink: 0, fontFamily: G.fontBrand }}>
                {company.name?.[0]?.toUpperCase()}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <p style={{ fontWeight: 700, fontSize: '15px', color: G.text }}>{company.name}</p>
                  <StatusPill status={company.status} />
                  <span style={{ fontSize: '11px', color: G.textFaint }}>{company.slug}</span>
                </div>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  {[
                    ['👥', company.employeeCount + ' employees'],
                    ['📋', company.activeTaskCount + ' active tasks'],
                    ['📦', company.plan],
                    company.subscriptionEnd && ['📅', `${Math.abs(company.daysUntilExpiry)}d ${company.daysUntilExpiry < 0 ? 'expired' : 'left'}`],
                  ].filter(Boolean).map(([icon, text], i) => (
                    <span key={i} style={{ fontSize: '12px', color: G.textMuted, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span>{icon}</span>{text}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: '12px', color: G.textMuted }}>Created {fmtDate(company.createdAt)}</p>
                <p style={{ fontSize: '11px', color: G.textFaint, marginTop: '2px' }}>SA: {company.superAdminEmployeeId}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data.totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px', alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pagBtn(page === 0)}>← Prev</button>
          <span style={{ fontSize: '13px', color: G.textMuted }}>Page {page + 1} of {data.totalPages}</span>
          <button onClick={() => setPage(p => Math.min(data.totalPages - 1, p + 1))} disabled={page >= data.totalPages - 1} style={pagBtn(page >= data.totalPages - 1)}>Next →</button>
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

const pagBtn = (disabled) => ({
  padding: '7px 16px', borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px', background: '#fff', color: disabled ? G.textFaint : G.textSub,
  border: `1px solid ${disabled ? '#E5E7EB' : G.borderStrong}`, opacity: disabled ? 0.5 : 1,
  fontWeight: 500,
});
